import { Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from '../features/auth/LoginPage';
import { SignupPage } from '../features/auth/SignupPage';
import { MerchantWalletPage } from '../features/wallet/WalletPage';
import { MerchantSettingsPage } from '../features/settings/MerchantSettingsPage';
import { MerchantLayout } from '../components/layout/MerchantLayout';
import { PickupRequestsPage } from '../features/pickup-requests/PickupRequestsPage';
import { ImpersonateBootstrap } from '../features/auth/ImpersonateBootstrap';
import { OnboardingWizard } from '../features/onboarding/OnboardingWizard';
import { MerchantDashboardPage } from '../features/dashboard/MerchantDashboardPage';
// Real list pages - same components admin uses, but they read useAuth and hide merchant column/dropdown when kind=merchant
import { OrdersB2cPage } from '../features/orders/OrdersB2cPage';
import { OrdersBucketPage } from '../features/orders/OrdersBucketPage';
import { RtoOrdersPage } from '../features/rto/RtoOrdersPage';
import { OrdersB2bPage } from '../features/orders/OrdersB2bPage';
import { CreateOrderB2cPage } from '../features/orders/CreateOrderB2cPage';
import { CreateOrderB2bPage } from '../features/orders/CreateOrderB2bPage';
import { OrderDetailB2cPage, OrderDetailB2bPage } from '../features/orders/OrderDetailPage';
import { NdrPage } from '../features/ndr/NdrPage';
import { TicketsPage } from '../features/tickets/TicketsPage';
import { HelpPage } from '../features/help/HelpPage';
import { RemittancePage } from '../features/remittance/RemittancePage';
import { WeightPage } from '../features/weight/WeightPage';
import { InvoicesUnifiedPage } from '../features/invoices/InvoicesUnifiedPage';
import { CreditNotesUnifiedPage } from '../features/credit-notes/CreditNotesUnifiedPage';
import { DebitNotesUnifiedPage } from '../features/debit-notes/DebitNotesUnifiedPage';
import { FreightUnifiedPage } from '../features/freight/FreightUnifiedPage';
import { PriceCalculatorPage } from '../features/price-calculator/PriceCalculatorPage';
import { MerchantServiceabilityPage } from '../features/serviceability/MerchantServiceabilityPage';
import { MerchantPickupLocationsPage } from '../features/warehouses/MerchantPickupLocationsPage';
import { MerchantApiPage } from '../features/merchant-api/MerchantApiPage';
import { ChannelsPage } from '../features/channels/ChannelsPage';
import { ConnectStorePage } from '../features/channels/ConnectStorePage';
import { CustomersPage } from '../features/customers/CustomersPage';
import { WmsRoutes } from '../features/wms/WmsRoutes';
import { BulkPodPage } from '../features/pod/BulkPodPage';
import { MerchantAppointmentsPage } from '../features/appointments/MerchantAppointmentsPage';
import { useAuth } from '../hooks/useAuth';

export function MerchantApp() {
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  return (
    <Routes>
      <Route path="/login" element={<LoginPage portal="merchant" />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/impersonate" element={<ImpersonateBootstrap />} />
      {/* Where a Shopify App Store install lands. Public and outside MerchantLayout on purpose: the
          merchant arrives straight from Shopify with no Gaint session, and this page is what gives
          them one — putting it behind the login guard would bounce the install to a dead end. */}
      <Route path="/connect-store" element={<ConnectStorePage />} />
      {/* Outside MerchantLayout on purpose: the wizard is the whole screen, and putting it inside the
          layout would show a nav the merchant is not yet allowed to use. */}
      <Route path="/onboarding" element={<OnboardingWizard />} />
      <Route path="/" element={<MerchantLayout />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard"        element={<MerchantDashboardPage />} />
        <Route path="orders"           element={<Navigate to="/orders-b2c" replace />} />
        <Route path="orders-b2c"       element={<OrdersB2cPage />} />
        <Route path="orders-b2c/new"   element={<CreateOrderB2cPage />} />
        <Route path="orders-b2c/:id"   element={<OrderDetailB2cPage />} />
        <Route path="orders-b2b"       element={<OrdersB2bPage />} />
        <Route path="orders-b2b/new"   element={<CreateOrderB2bPage />} />
        <Route path="orders-b2b/:id"   element={<OrderDetailB2bPage />} />
        <Route path="orders-bucket"    element={<OrdersBucketPage variant="merchant" />} />
        <Route path="rto-orders"       element={<RtoOrdersPage />} />
        <Route path="appointments"     element={<MerchantAppointmentsPage />} />
        <Route path="ndr"              element={<NdrPage />} />
        <Route path="tickets"          element={<TicketsPage />} />
        <Route path="help"             element={<HelpPage />} />
        <Route path="remittance"       element={<RemittancePage />} />
        {/* The Weight link was already in the merchant nav but had no route behind it, so the
            module simply did not open. The API has always scoped `/weight` to the caller's own
            merchantId, so this shows their cases only. */}
        <Route path="weight"           element={<WeightPage />} />
        {/* Same bug as Weight above: MerchantLayout has always shown a "POD Download" link to /pod,
            but no route answered it, so the click fell through to the `*` catch-all below and
            redirected the merchant to /login — which reads as being logged out at random. The page
            itself is already merchant-aware (it hides the bulk ZIP, which the API refuses for them
            anyway, and retitles itself "POD Download"); it was only ever missing this line. */}
        <Route path="pod"              element={<BulkPodPage />} />
        <Route path="invoices"         element={<InvoicesUnifiedPage />} />
        <Route path="credit-notes"     element={<CreditNotesUnifiedPage />} />
        <Route path="debit-notes"      element={<DebitNotesUnifiedPage />} />
        <Route path="freight"          element={<FreightUnifiedPage />} />
        <Route path="price-calculator" element={<PriceCalculatorPage />} />
        <Route path="serviceability"   element={<MerchantServiceabilityPage />} />
        <Route path="warehouses"       element={<MerchantPickupLocationsPage />} />
        <Route path="pickup-requests" element={<PickupRequestsPage />} />
        <Route path="customers"        element={<CustomersPage />} />
        <Route path="api"              element={<MerchantApiPage />} />
        <Route path="channels"         element={<ChannelsPage />} />
        <Route path="api-docs"         element={<MerchantApiPage />} />
        <Route path="wallet"           element={<MerchantWalletPage />} />
        <Route path="wms/*"            element={<WmsRoutes />} />
        <Route path="settings"         element={<MerchantSettingsPage />} />
      </Route>
      {/* A signed-in merchant who lands on an unknown path goes to their dashboard, NOT to /login.
          Sending an authenticated user to the login screen looks exactly like being logged out, and
          it turned a missing route (see /pod above) into a reported auth bug. Only genuinely
          signed-out visitors are sent to log in. */}
      <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
    </Routes>
  );
}

